import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Users, Mail, Settings, LogOut, ShieldCheck, ShieldOff,
  Trash2, Pencil, Send, Eye, Plus, Loader2, X, Check, AlertCircle, RefreshCw, Sparkles,
  Link2, FileText, Image as ImageIcon, Calendar, Upload, Key, Copy, BarChart3, TrendingUp, TrendingDown,
  Instagram, Facebook, Youtube, Linkedin, Twitter, User as UserIcon, Heart, ExternalLink
} from 'lucide-react';
import { SiTiktok } from 'react-icons/si';
import { ApiDocs } from '../admin/ApiDocs';
import { useAdminPosts } from '../admin/useAdminPosts';
import { PostMedia } from '../admin/PostMedia';
import { PostCalendarTab } from '../admin/PostCalendarTab';
import { TikTokPublishOptions, tiktokConfig } from '../TikTokPublishOptions';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'autopostr_admin_token';

const fetchAdmin = (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY) || '';
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Token': token,
      ...(opts.headers || {}),
    },
  });
};

// ===== Login =====
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin-panel/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const d = await r.json();
      if (r.ok && d.token) {
        localStorage.setItem(TOKEN_KEY, d.token);
        onLogin();
      } else {
        setError(d.detail || 'Login failed');
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#141414] border border-white/10 rounded-2xl p-8">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck size={22} className="text-emerald-400" />
          </div>
          <h1 className="text-xl font-bold text-white">AutoPostr Admin Panel</h1>
          <p className="text-white/50 text-sm mt-1">Restricted access</p>
        </div>
        <input
          type="password"
          placeholder="Admin password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-login-password"
          className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none mb-3"
          autoFocus
        />
        {error && <p className="text-red-400 text-sm mb-3 text-center">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-submit"
          className="w-full py-3 rounded-xl bg-emerald-400 hover:bg-emerald-500 disabled:opacity-50 text-black font-semibold flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
          Sign In
        </button>
      </form>
    </div>
  );
};

// ===== Users tab =====
const UsersTab = ({ toast }) => {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | free
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchAdmin('/api/admin-panel/users');
    const d = await r.json();
    setUsers(d.users || []);
    setSummary(d.summary || {});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const grantPro = async (u) => {
    const plan = window.prompt('Plan: "monthly" or "yearly"', 'monthly');
    if (!plan) return;
    const days = parseInt(window.prompt('Days of access', plan === 'yearly' ? '365' : '30'), 10) || 30;
    const r = await fetchAdmin(`/api/admin-panel/users/${u.id || u.autopostr_id}/grant-pro`, {
      method: 'POST',
      body: JSON.stringify({ plan, days }),
    });
    const d = await r.json();
    toast(d.success ? `Pro granted to ${u.email}` : `Failed: ${d.detail || 'unknown'}`, d.success);
    load();
  };

  const revokePro = async (u) => {
    if (!window.confirm(`Revoke Pro for ${u.email}?`)) return;
    const r = await fetchAdmin(`/api/admin-panel/users/${u.id || u.autopostr_id}/revoke-pro`, { method: 'POST' });
    const d = await r.json();
    toast(d.success ? `Pro revoked for ${u.email}` : `Failed: ${d.detail || 'unknown'}`, d.success);
    load();
  };

  const delUser = async (u) => {
    if (!window.confirm(
      `Delete ${u.email}?\n\n` +
      `• Their Pro subscription will be revoked (if active)\n` +
      `• They will be hidden from this admin panel\n` +
      `• Note: api.autopostr.co.uk has no hard-delete endpoint, so the row stays in MongoDB ` +
      `(purge via MongoDB Atlas to fully remove).`
    )) return;
    const r = await fetchAdmin(`/api/admin-panel/users/${u.id || u.local_id || u.autopostr_id}`, { method: 'DELETE' });
    const d = await r.json();
    if (d.success) {
      toast(`Deleted ${u.email}` + (d.pro_revoked_on_autopostr ? ' · Pro revoked' : ''), true);
    } else {
      toast(`Failed: ${d.detail || 'unknown'}`, false);
    }
    load();
  };

  const saveEdit = async () => {
    const r = await fetchAdmin(`/api/admin-panel/users/${editing.id || editing.local_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name: editing._name, email: editing._email }),
    });
    const d = await r.json();
    toast(d.success ? 'Saved' : `Failed: ${d.detail}`, d.success);
    setEditing(null);
    load();
  };

  const filtered = users.filter(u => {
    const text = (u.email + ' ' + (u.name || '')).toLowerCase();
    if (q && !text.includes(q.toLowerCase())) return false;
    if (filter === 'active' && u.subscription_status !== 'active') return false;
    if (filter === 'free' && u.subscription_status === 'active') return false;
    return true;
  });

  return (
    <div data-testid="admin-users-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatCard label="Total users" value={summary.total || 0} />
        <StatCard label="Active Pro" value={summary.active || 0} accent="emerald" />
        <StatCard label="Free" value={summary.free || 0} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name..."
            data-testid="admin-users-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/40 focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <div className="flex gap-1 bg-white/5 rounded-lg p-1">
          {['all', 'active', 'free'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-sm ${filter === f ? 'bg-emerald-500 text-black' : 'text-white/60 hover:text-white'}`}
            >
              {f === 'all' ? 'All' : f === 'active' ? 'Pro' : 'Free'}
            </button>
          ))}
        </div>
        <button onClick={load} className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-8 text-white/40">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-white/40">No users</td></tr>
              ) : filtered.map(u => (
                <tr key={u.id + u.email} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white font-medium">{u.email}</td>
                  <td className="px-4 py-3 text-white/70">{u.name || '—'}</td>
                  <td className="px-4 py-3">
                    {u.subscription_status === 'active' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-semibold">PRO</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/5 text-white/50 text-xs">Free</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/50 text-xs">
                    {u.subscription_expiry ? new Date(u.subscription_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3"><SourceBadge src={u.source} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {u.subscription_status === 'active' ? (
                        <IconBtn title="Revoke Pro" onClick={() => revokePro(u)} icon={ShieldOff} color="amber" />
                      ) : (
                        <IconBtn title="Grant Pro" onClick={() => grantPro(u)} icon={ShieldCheck} color="emerald" />
                      )}
                      <IconBtn title="Edit" onClick={() => setEditing({ ...u, _name: u.name || '', _email: u.email })} icon={Pencil} />
                      <IconBtn title="Delete" onClick={() => delUser(u)} icon={Trash2} color="red" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-white/5 text-white/40 text-xs">
          Showing {filtered.length} of {users.length}
        </div>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit user (local DB only)">
          <p className="text-white/50 text-xs mb-3">
            <AlertCircle size={12} className="inline mr-1" />
            Edits only apply to the local Emergent DB. api.autopostr.co.uk has no admin edit endpoint.
          </p>
          <label className="block text-white/60 text-xs mb-1">Email</label>
          <input value={editing._email} onChange={(e) => setEditing({ ...editing, _email: e.target.value })}
            className="w-full p-2.5 rounded-lg bg-white/5 border border-white/10 text-white mb-3" />
          <label className="block text-white/60 text-xs mb-1">Name</label>
          <input value={editing._name} onChange={(e) => setEditing({ ...editing, _name: e.target.value })}
            className="w-full p-2.5 rounded-lg bg-white/5 border border-white/10 text-white mb-4" />
          <button onClick={saveEdit} className="w-full py-2.5 rounded-lg bg-emerald-400 text-black font-semibold">Save</button>
        </Modal>
      )}
    </div>
  );
};

// ===== Campaigns tab =====
const CampaignsTab = ({ toast }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchAdmin('/api/admin-panel/campaigns');
    const d = await r.json();
    setCampaigns(d.campaigns || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const blank = {
    name: '',
    subject: '',
    html_body: `<div style="font-family: -apple-system, Arial, sans-serif; max-width: 560px; margin: 0 auto; padding: 32px 24px; color: #1a1a1a;">
  <h1 style="color: #10b981;">Hey {{name}}!</h1>
  <p>This is your AutoPostr campaign email.</p>
  <p>You are currently on the <strong>{{plan}}</strong> plan.</p>
  <p><a href="https://autopostr.co.uk/pricing" style="display: inline-block; padding: 12px 24px; background: #10b981; color: #000; border-radius: 999px; text-decoration: none; font-weight: 600;">Upgrade to Pro</a></p>
  <hr style="margin: 32px 0; border: 0; border-top: 1px solid #eee;">
  <p style="color: #999; font-size: 12px;">Sent to {{email}} · AutoPostr · <a href="https://autopostr.co.uk">autopostr.co.uk</a></p>
</div>`,
    segment: 'all',
    custom_emails: '',
  };

  const create = async () => {
    const payload = {
      name: editing.name,
      subject: editing.subject,
      html_body: editing.html_body,
      segment: editing.segment,
      custom_emails: editing.segment === 'custom'
        ? (editing.custom_emails || '').split(/[,\s]+/).filter(Boolean)
        : null,
    };
    const r = await fetchAdmin('/api/admin-panel/campaigns', { method: 'POST', body: JSON.stringify(payload) });
    const d = await r.json();
    if (r.ok) { toast('Campaign saved', true); setEditing(null); load(); }
    else toast(d.detail || 'Failed', false);
  };

  const sendNow = async (c) => {
    if (!window.confirm(`Send "${c.name}" to all matching recipients NOW?`)) return;
    toast('Sending… this may take a while', true);
    const r = await fetchAdmin(`/api/admin-panel/campaigns/${c.id}/send-now`, { method: 'POST' });
    const d = await r.json();
    if (r.ok) toast(`Sent ${d.sent}, failed ${d.failed}, recipients ${d.recipients}`, true);
    else toast(d.detail || 'Failed', false);
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await fetchAdmin(`/api/admin-panel/campaigns/${c.id}`, { method: 'DELETE' });
    toast('Deleted', true);
    load();
  };

  return (
    <div data-testid="admin-campaigns-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Email Campaigns</h2>
        <button
          onClick={() => setEditing(blank)}
          data-testid="admin-new-campaign-btn"
          className="px-4 py-2 rounded-lg bg-emerald-400 hover:bg-emerald-500 text-black font-semibold flex items-center gap-2"
        >
          <Plus size={16} /> New campaign
        </button>
      </div>

      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Segment</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Sent</th>
              <th className="text-left px-4 py-3">Opens</th>
              <th className="text-left px-4 py-3">Clicks</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center py-8 text-white/40">Loading…</td></tr>
            ) : campaigns.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-white/40">No campaigns yet. Create one above.</td></tr>
            ) : campaigns.map(c => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-white">{c.name}<div className="text-white/40 text-xs">{c.subject}</div></td>
                <td className="px-4 py-3 text-white/70 text-xs">{c.segment}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${c.status === 'sent' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-white/50'}`}>{c.status}</span>
                </td>
                <td className="px-4 py-3 text-white/70">{c.sent_count || 0}</td>
                <td className="px-4 py-3 text-white/70">{c.open_count || 0}</td>
                <td className="px-4 py-3 text-white/70">{c.click_count || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <IconBtn title="Preview" onClick={() => setPreview(c)} icon={Eye} />
                    {c.status !== 'sent' && <IconBtn title="Send now" onClick={() => sendNow(c)} icon={Send} color="emerald" />}
                    <IconBtn title="Delete" onClick={() => remove(c)} icon={Trash2} color="red" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title="New campaign" onClose={() => setEditing(null)} wide>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input placeholder="Campaign name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-white" />
            <select value={editing.segment} onChange={(e) => setEditing({ ...editing, segment: e.target.value })} className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-white">
              <option value="all">All users</option>
              <option value="free">Free users only</option>
              <option value="pro">Pro users only</option>
              <option value="expiring">Pro expiring in next 7 days</option>
              <option value="custom">Custom email list</option>
            </select>
          </div>
          <input placeholder="Subject (supports {{name}}, {{email}}, {{plan}})" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="w-full p-2.5 rounded-lg bg-white/5 border border-white/10 text-white mb-3" />
          {editing.segment === 'custom' && (
            <textarea placeholder="Paste emails separated by commas or new lines" value={editing.custom_emails} onChange={(e) => setEditing({ ...editing, custom_emails: e.target.value })} rows={3} className="w-full p-2.5 rounded-lg bg-white/5 border border-white/10 text-white mb-3" />
          )}
          <label className="block text-white/60 text-xs mb-1">HTML body — variables: {`{{name}} {{email}} {{plan}}`}</label>
          <textarea value={editing.html_body} onChange={(e) => setEditing({ ...editing, html_body: e.target.value })} rows={14} className="w-full p-2.5 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-xs mb-4" />
          <div className="flex gap-2">
            <button onClick={create} className="flex-1 py-2.5 rounded-lg bg-emerald-400 text-black font-semibold">Save campaign</button>
          </div>
        </Modal>
      )}

      {preview && (
        <Modal title={`Preview · ${preview.name}`} onClose={() => setPreview(null)} wide>
          <p className="text-white/50 text-xs mb-2">Subject: <span className="text-white">{preview.subject}</span></p>
          <div className="bg-white rounded-lg p-6 overflow-auto max-h-[60vh]">
            <div dangerouslySetInnerHTML={{ __html: preview.html_body }} />
          </div>
        </Modal>
      )}
    </div>
  );
};

// ===== Pro Watcher tab =====
const ProWatcherTab = ({ toast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchAdmin('/api/admin-panel/pro-watcher/status');
    const d = await r.json();
    setData(d);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const r = await fetchAdmin('/api/admin-panel/pro-watcher/template', {
      method: 'PATCH',
      body: JSON.stringify({
        subject: data.subject,
        html_body: data.html_body,
        enabled: data.enabled,
      }),
    });
    const d = await r.json();
    toast(d.success ? 'Template saved' : `Failed: ${d.detail || 'unknown'}`, d.success);
    setSaving(false);
  };

  const runNow = async () => {
    if (!window.confirm('Run watcher cycle NOW? This will email any Pro user not yet notified.')) return;
    setRunning(true);
    const r = await fetchAdmin('/api/admin-panel/pro-watcher/run-now', { method: 'POST' });
    const d = await r.json();
    if (d.error) toast(`Error: ${d.error}`, false);
    else toast(`Sent ${d.sent}, skipped ${d.skipped}, failed ${d.failed}`, true);
    setRunning(false);
    load();
  };

  const sendTest = async () => {
    if (!testEmail) return;
    const r = await fetchAdmin('/api/admin-panel/pro-watcher/send-test', {
      method: 'POST',
      body: JSON.stringify({ to: testEmail }),
    });
    const d = await r.json();
    toast(d.sent ? `Test sent to ${testEmail}` : 'Test failed', d.sent);
  };

  if (loading || !data) return <div className="text-white/40 text-center py-12">Loading…</div>;

  return (
    <div data-testid="admin-pro-watcher-tab" className="max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Sparkles size={20} className="text-yellow-400" />
        <h2 className="text-xl font-bold text-white">Pro Activation Watcher</h2>
      </div>

      <p className="text-white/50 text-sm mb-6">
        Polls api.autopostr.co.uk every {data.interval_seconds}s, detects newly-activated Pro users
        (from Apple IAP, Stripe web, Google Play, or manual admin grants) and sends them the
        <em> Welcome to Pro </em>email below. De-duped — each user only ever receives it once.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatCard label="Total notified" value={data.notified_total || 0} accent="emerald" />
        <StatCard label="Last run sent" value={data.last_runs?.[0]?.sent ?? '—'} />
        <StatCard label="SMTP" value={data.smtp_configured ? 'OK' : 'Not configured'} />
      </div>

      {/* Enable / disable toggle */}
      <div className="bg-[#141414] border border-white/10 rounded-xl p-4 mb-6 flex items-center justify-between">
        <div>
          <p className="text-white font-medium">Watcher is {data.enabled ? 'ENABLED' : 'DISABLED'}</p>
          <p className="text-white/40 text-xs">Toggle off to pause all welcome emails without losing the template.</p>
        </div>
        <button
          onClick={() => setData({ ...data, enabled: !data.enabled })}
          data-testid="pro-watcher-toggle"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${data.enabled ? 'bg-emerald-500' : 'bg-white/10'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${data.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Template editor */}
      <h3 className="text-white font-semibold mb-3">Welcome email template</h3>
      <p className="text-white/40 text-xs mb-3">Variables: <code className="bg-white/10 px-1 rounded">{`{{name}}`}</code>, <code className="bg-white/10 px-1 rounded">{`{{email}}`}</code>, <code className="bg-white/10 px-1 rounded">{`{{plan}}`}</code>, <code className="bg-white/10 px-1 rounded">{`{{expires}}`}</code></p>

      <label className="block text-white/60 text-xs mb-1">Subject</label>
      <input
        value={data.subject}
        onChange={(e) => setData({ ...data, subject: e.target.value })}
        className="w-full p-2.5 rounded-lg bg-white/5 border border-white/10 text-white mb-3"
        data-testid="pro-watcher-subject"
      />

      <label className="block text-white/60 text-xs mb-1">HTML body</label>
      <textarea
        value={data.html_body}
        onChange={(e) => setData({ ...data, html_body: e.target.value })}
        rows={16}
        className="w-full p-2.5 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-xs mb-4"
        data-testid="pro-watcher-body"
      />

      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={save} disabled={saving} className="px-4 py-2.5 rounded-lg bg-emerald-400 hover:bg-emerald-500 disabled:opacity-50 text-black font-semibold flex items-center gap-2" data-testid="pro-watcher-save">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          Save template
        </button>
        <div className="flex gap-2">
          <input
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@example.com"
            className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
          />
          <button onClick={sendTest} disabled={!testEmail} className="px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm disabled:opacity-50 flex items-center gap-2">
            <Eye size={14} /> Send preview
          </button>
        </div>
        <button onClick={runNow} disabled={running} className="px-4 py-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/15 text-yellow-400 text-sm flex items-center gap-2">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Run watcher now
        </button>
      </div>

      {/* Recent runs log */}
      <h3 className="text-white font-semibold mb-3">Recent watcher runs</h3>
      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Sent</th>
              <th className="text-left px-4 py-2">Skipped</th>
              <th className="text-left px-4 py-2">Failed</th>
              <th className="text-left px-4 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {(data.last_runs || []).length === 0 ? (
              <tr><td colSpan="5" className="text-center py-4 text-white/40">No runs yet</td></tr>
            ) : data.last_runs.map((r, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="px-4 py-2 text-white/70 text-xs">{new Date(r.ran_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-emerald-400">{r.sent}</td>
                <td className="px-4 py-2 text-white/40">{r.skipped}</td>
                <td className="px-4 py-2 text-red-400">{r.failed}</td>
                <td className="px-4 py-2 text-white/50 text-xs">{r.error || (r.force_resend ? 'force resend' : 'normal cycle')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===== SMTP / Settings tab =====
const SettingsTab = ({ toast }) => {
  const [status, setStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAdmin('/api/admin-panel/smtp-status').then(r => r.json()).then(setStatus);
  }, []);

  const test = async () => {
    if (!testEmail) return;
    setSending(true);
    const r = await fetchAdmin('/api/admin-panel/smtp-test', { method: 'POST', body: JSON.stringify({ to: testEmail }) });
    const d = await r.json();
    toast(d.sent ? `Sent to ${testEmail}` : 'Failed - check backend logs', d.sent);
    setSending(false);
  };

  return (
    <div data-testid="admin-settings-tab" className="max-w-2xl">
      <h2 className="text-xl font-bold text-white mb-6">SMTP Settings</h2>

      <div className="bg-[#141414] border border-white/10 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          {status?.configured ? (
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">Configured</span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold">Not configured</span>
          )}
          <span className="text-white/40 text-xs">Set environment variables on the backend</span>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <KV k="Host" v={status?.host || '(not set)'} />
          <KV k="Port" v={status?.port || '587'} />
          <KV k="User" v={status?.user || '(not set)'} />
          <KV k="From" v={status?.from || '(not set)'} />
          <KV k="From name" v={status?.from_name || 'AutoPostr'} />
        </div>
      </div>

      <h3 className="text-white font-semibold mb-2">Required environment variables</h3>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-xl p-4 font-mono text-xs text-white/70 mb-6">
        SMTP_HOST=smtp.yourprovider.com<br />
        SMTP_PORT=587  (587 STARTTLS or 465 TLS)<br />
        SMTP_USER=your-username<br />
        SMTP_PASS=your-password<br />
        SMTP_FROM=noreply@autopostr.co.uk<br />
        SMTP_FROM_NAME=AutoPostr
      </div>

      <h3 className="text-white font-semibold mb-3">Send test email</h3>
      <div className="flex gap-2">
        <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="flex-1 p-2.5 rounded-lg bg-white/5 border border-white/10 text-white" />
        <button onClick={test} disabled={sending || !status?.configured} className="px-4 py-2.5 rounded-lg bg-emerald-400 hover:bg-emerald-500 disabled:opacity-50 text-black font-semibold flex items-center gap-2">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          Send test
        </button>
      </div>
    </div>
  );
};

// ===== Helpers =====
const StatCard = ({ label, value, accent }) => (
  <div className={`p-4 rounded-xl border ${accent === 'emerald' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/5 border-white/10'}`}>
    <div className="text-white/40 text-xs uppercase tracking-wider">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${accent === 'emerald' ? 'text-emerald-400' : 'text-white'}`}>{value}</div>
  </div>
);

const SourceBadge = ({ src }) => {
  const map = { autopostr: 'bg-blue-500/10 text-blue-400 border-blue-500/30', local: 'bg-purple-500/10 text-purple-400 border-purple-500/30', both: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', api: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' };
  const cls = map[src] || 'bg-white/5 text-white/40';
  return <span className={`px-2 py-0.5 rounded text-xs border ${cls}`}>{src}</span>;
};

const IconBtn = ({ title, onClick, icon: Icon, color }) => {
  const colors = {
    emerald: 'hover:bg-emerald-500/10 hover:text-emerald-400',
    amber: 'hover:bg-amber-500/10 hover:text-amber-400',
    red: 'hover:bg-red-500/10 hover:text-red-400',
  };
  return (
    <button onClick={onClick} title={title} className={`p-2 rounded-lg text-white/40 transition-colors ${colors[color] || 'hover:bg-white/10 hover:text-white'}`}>
      <Icon size={14} />
    </button>
  );
};

const Modal = ({ children, title, onClose, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
    <div className={`bg-[#141414] border border-white/10 rounded-2xl p-6 mt-12 w-full ${wide ? 'max-w-3xl' : 'max-w-md'}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">{title}</h3>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 text-white/60"><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

const KV = ({ k, v }) => (
  <div>
    <div className="text-white/40 text-xs uppercase tracking-wider">{k}</div>
    <div className="text-white text-sm truncate">{v}</div>
  </div>
);

// ===== Connections tab (admin view of every user's connected socials, live from Chat33) =====
const PLATFORM_META = {
  tiktok:    { label: 'TikTok',    Icon: SiTiktok,  color: '#ffffff' },
  instagram: { label: 'Instagram', Icon: Instagram, color: '#E4405F' },
  facebook:  { label: 'Facebook',  Icon: Facebook,  color: '#1877F2' },
  youtube:   { label: 'YouTube',   Icon: Youtube,   color: '#FF0033' },
  x:         { label: 'X',         Icon: Twitter,   color: '#ffffff' },
  twitter:   { label: 'X',         Icon: Twitter,   color: '#ffffff' },
  linkedin:  { label: 'LinkedIn',  Icon: Linkedin,  color: '#0A66C2' },
};

const PlatformLogo = ({ platform, size = 14, className = '' }) => {
  const key = (platform || '').toLowerCase();
  const meta = PLATFORM_META[key];
  if (!meta) return <Link2 size={size} className={`text-white/60 ${className}`} />;
  const { Icon, color } = meta;
  return <Icon size={size} color={color} className={className} aria-label={meta.label} />;
};

// Legacy map kept for the few call-sites that still want an emoji fallback (none now).
const PLATFORM_ICONS = {
  tiktok: '🎵', facebook: '📘', instagram: '📷', youtube: '▶️', x: '𝕏', linkedin: '💼',
};

const ConnectionsTab = ({ toast }) => {
  const [data, setData] = useState({ users: [], total_users: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchAdmin('/api/admin-panel/all-accounts');
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setData(d);
    } catch (e) { toast(`Load failed: ${e.message}`, false); }
    setLoading(false);
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.users.filter(u =>
    !q || (u.email || '').toLowerCase().includes(q.toLowerCase()) || (u.name || '').toLowerCase().includes(q.toLowerCase())
  );

  const totalAccounts = data.users.reduce((s, u) => s + (u.account_count || 0), 0);

  return (
    <div data-testid="admin-connections">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Connections</h2>
          <p className="text-white/50 text-sm mt-1">Every user and their social accounts — synced live</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right text-sm">
            <div className="text-white font-semibold">{data.total_users} users</div>
            <div className="text-white/40 text-xs">{totalAccounts} accounts connected</div>
          </div>
          <button onClick={load} data-testid="connections-refresh" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email or name…"
          data-testid="connections-search"
          className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/50"><Loader2 className="animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-white/40">No users connected yet.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(u => (
            <div key={u.user_id} data-testid={`connection-row-${u.user_id}`} className="bg-[#141414] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-white font-semibold">{u.email || 'no-email'}</div>
                  <div className="text-white/40 text-xs">{u.name || '—'} · {u.account_count} {u.account_count === 1 ? 'account' : 'accounts'}{u.external_id ? ` · ext: ${u.external_id}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  {u.created_via === 'api_v1' && <span data-testid={`via-api-badge-${u.user_id}`} className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">via API</span>}
                  {u.status === 'not_connected' && <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-400">needs reconnect</span>}
                  {u.status === 'error' && <span className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400">provider error</span>}
                  {u.error && <span className="text-xs text-red-400 max-w-[280px] truncate" title={u.error}>{u.error}</span>}
                </div>
              </div>
              {u.accounts.length === 0 ? (
                <div className="text-white/30 text-xs italic">
                  {u.status === 'not_connected'
                    ? 'No profile yet — ask this user to click "Add account" on their AutoPostr dashboard.'
                    : 'No connected accounts.'}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {u.accounts.map(a => (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                      <PlatformLogo platform={a.platform} size={16} />
                      <span className="text-white text-sm">{a.username || a.display_name || a.id.slice(0, 8)}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${a.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-white/10 text-white/40'}`}>{a.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ===== Posts tab (admin grid + filters + bulk delete + Create-post modal) =====
const PostsTab = ({ toast, profileId, onProfileChange }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [failedCancels, setFailedCancels] = useState([]); // [{id, error}] from last bulk delete
  const [retrying, setRetrying] = useState(false);
  const feed = useAdminPosts(fetchAdmin, toast, showCreate || deleting || retrying, profileId);
  const { posts, loading, refreshing, syncing, load, profiles, query, total, page, has_more: hasMore } = feed;
  const [accountMap, setAccountMap] = useState({});
  useEffect(() => {
    setAccountMap({});
    if (query.user_id === 'all') return undefined;
    const controller = new AbortController();
    fetchAdmin(`/api/admin-panel/post-profiles/${encodeURIComponent(query.user_id)}/accounts`, { signal: controller.signal })
      .then(async r => { if (!r.ok) return null; return r.json(); })
      .then(d => {
        if (d && !controller.signal.aborted) setAccountMap(Object.fromEntries((d.accounts || []).map(a => [a.id, a])));
      }).catch(() => {});
    return () => controller.abort();
  }, [query.user_id]);
  const filtered = posts;
  useEffect(() => { setSelected(new Set()); }, [feed.queryKey]);
  useEffect(() => {
    setSelected(previous => new Set([...previous].filter(id => posts.some(p => p.id === id))));
  }, [posts]);

  // Platforms for a post = unique set of platforms across its account_ids.
  // Prefer server-side snapshots so historical account IDs still resolve.
  const postPlatforms = (p) => {
    const s = new Set();
    const snapById = {};
    (p.account_snapshots || []).forEach(sn => { if (sn.id) snapById[sn.id] = sn; });
    (p.account_ids || []).forEach(id => {
      const info = snapById[id] || accountMap[id];
      if (info?.platform) s.add(info.platform);
    });
    // Also pick up platforms from chat33_posts snapshots (some have .platform directly)
    (p.chat33_posts || []).forEach(cp => { if (cp.platform) s.add(cp.platform); });
    return Array.from(s);
  };

  const StatusPill = (statusVal) => (
    statusVal === 'published' ? 'bg-emerald-500/10 text-emerald-400'
    : statusVal === 'scheduled' ? 'bg-blue-500/10 text-blue-400'
    : statusVal === 'publishing' ? 'bg-amber-500/10 text-amber-400'
    : statusVal === 'failed' ? 'bg-red-500/10 text-red-400'
    : statusVal === 'cancelled' ? 'bg-white/5 text-white/40 line-through'
    : 'bg-white/10 text-white/50'
  );

  const KNOWN_STATUSES = ['draft', 'scheduled', 'publishing', 'published', 'draft_uploaded', 'failed', 'cancelled'];
  const scheduleOf = (p) => p.schedule_time || p.scheduled_at || null;

  // Status comes from the backend (synced live from the provider). Fall back to
  // inferring from the schedule time only for rows the backend couldn't classify.
  const computedStatus = (p) => {
    const stored = (p.status || '').toLowerCase();
    if (KNOWN_STATUSES.includes(stored)) return stored;
    const st = scheduleOf(p);
    if (st) {
      const t = new Date(st).getTime();
      if (!isNaN(t)) return t > Date.now() ? 'scheduled' : 'published';
    }
    if ((p.chat33_post_ids || []).length || p.chat33_post_id) return 'published';
    return 'draft';
  };

  const timeInfo = (p) => {
    const valid = value => value && !Number.isNaN(new Date(value).getTime());
    const fallback = [p.published_at, p.schedule_time, p.scheduled_at, p.created_at].find(valid);
    const stamp = p.timeline_at || fallback;
    const label = valid(p.published_at) ? 'Posted' : valid(p.schedule_time) || valid(p.scheduled_at) ? 'Scheduled for' : 'Created';
    return { label: stamp ? label : 'No date recorded', value: stamp ? new Date(stamp).toLocaleString() : '—' };
  };

  const platformChips = (p) => {
    // Resolve the per-account meta: server-side snapshots take priority (they
    // include historical accounts whose user later disconnected), then the
    // live /all-accounts map.
    const snapById = {};
    (p.account_snapshots || []).forEach(s => {
      if (s.id) snapById[s.id] = { platform: s.platform, username: s.username };
    });
    const metaFor = (aid) => ({
      ...accountMap[aid], ...snapById[aid],
      platform: snapById[aid]?.platform || accountMap[aid]?.platform,
      username: snapById[aid]?.username || accountMap[aid]?.username || '',
    });

    const snaps = p.chat33_posts || [];
    if (snaps.length) {
      const fallback = postPlatforms(p);
      return snaps
        .map((s, i) => {
          const info = metaFor(s.account_id);
          return {
            platform: s.platform || info.platform || fallback[i] || fallback[0],
            username: info.username || '',
            status: s.status,
            error: s.error,
            url: s.external_url,
          };
        })
        .filter(c => c.platform);
    }
    // Fallback: build chips directly from account_ids so we still get usernames
    return (p.account_ids || []).map(id => {
      const info = metaFor(id);
      return info.platform ? { platform: info.platform, username: info.username || '', status: null } : null;
    }).filter(Boolean);
  };
  const dotColor = (s) => (
    s === 'published' ? 'bg-emerald-400' : s === 'scheduled' ? 'bg-blue-400' : s === 'publishing' ? 'bg-amber-400'
    : s === 'failed' ? 'bg-red-400' : s === 'cancelled' ? 'bg-white/30' : 'bg-white/20'
  );

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const selectAll = () => setSelected(new Set(filtered.map(p => p.id)));
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} ${ids.length === 1 ? 'post' : 'posts'}? Scheduled posts will also be cancelled with the provider.`)) return;
    setDeleting(true);
    try {
      const r = await fetchAdmin('/api/admin-panel/posts', {
        method: 'DELETE', body: JSON.stringify({ post_ids: ids }),
      });
      const d = await r.json();
      // 207: partial failure — some scheduled posts couldn't be cancelled on the provider
      if (r.status === 207 || (d && d.detail && d.detail.failed && d.detail.failed.length)) {
        const detail = d.detail || d;
        const failed = detail.failed || [];
        setFailedCancels(failed);
        toast(`Deleted ${detail.deleted}/${detail.requested} · ${failed.length} still queued on provider — retry below`, false);
      } else if (!r.ok) {
        throw new Error(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail || d));
      } else {
        setFailedCancels([]);
        const missing = (d.missing || []).length;
        toast(`Deleted ${d.deleted}/${d.requested}${missing ? ` · ${missing} already gone` : ''}`, true);
      }
      load();
    } catch (e) { toast(`Delete failed: ${e.message}`, false); }
    setDeleting(false);
  };

  const retryCancel = async (ids) => {
    if (ids.length === 0) return;
    setRetrying(true);
    try {
      const r = await fetchAdmin('/api/admin-panel/posts/retry-cancel', {
        method: 'POST', body: JSON.stringify({ post_ids: ids }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      const cancelled = new Set(d.cancelled || []);
      setFailedCancels(prev => prev.filter(f => !cancelled.has(f.id)).concat(d.still_failed || []).filter((v, i, arr) => arr.findIndex(x => x.id === v.id) === i));
      toast(`Cancelled ${d.cancelled.length}${d.still_failed.length ? ` · ${d.still_failed.length} still failing` : ''}`, d.still_failed.length === 0);
      load();
    } catch (e) { toast(`Retry failed: ${e.message}`, false); }
    setRetrying(false);
  };

  return (
    <div data-testid="admin-posts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Posts</h2>
          <p data-testid="posts-count" className="text-white/50 text-sm mt-1">{loading ? 'Loading posts…' : `${total} ${total === 1 ? 'post' : 'posts'}${query.user_id === 'all' ? ' · All profiles' : ' · Selected profile'}`}</p>
          <p data-testid="posts-sort-order" className="text-white/35 text-xs mt-1">Newest first · {Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(refreshing || syncing) && <span data-testid="posts-sync-indicator" className="text-white/40 text-xs flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> {syncing ? 'Checking statuses' : 'Refreshing'}</span>}
          <button
            onClick={() => feed.syncVisible(true)}
            disabled={syncing || loading || !posts.length}
            data-testid="posts-backfill-status"
            title="Refresh provider statuses for this page only"
            className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-sm flex items-center gap-2">
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Sync this page
          </button>
          <button onClick={() => load()} data-testid="posts-refresh" aria-label="Refresh posts" title="Refresh posts" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="admin-create-post-btn"
            className="px-4 py-2 rounded-xl bg-emerald-400 hover:bg-emerald-500 text-black font-semibold flex items-center gap-2 text-sm">
            <Plus size={16} /> Create post
          </button>
        </div>
      </div>

      {/* Failed-cancel retry panel */}
      {failedCancels.length > 0 && (
        <div data-testid="failed-cancel-panel" className="bg-red-500/5 border border-red-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-red-300 font-semibold text-sm flex items-center gap-2">
                <AlertCircle size={14} /> {failedCancels.length} {failedCancels.length === 1 ? 'post is' : 'posts are'} still queued on the provider
              </div>
              <p className="text-white/50 text-xs mt-1">Chat33 refused to cancel these. They will still fire unless you retry.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => retryCancel(failedCancels.map(f => f.id))}
                disabled={retrying}
                data-testid="retry-cancel-all"
                className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2">
                {retrying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Retry all
              </button>
              <button onClick={() => setFailedCancels([])} data-testid="dismiss-failed-cancels" aria-label="Dismiss cancellation errors" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60">
                <X size={14} />
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {failedCancels.map(f => (
              <div key={f.id} data-testid={`failed-cancel-row-${f.id}`} className="flex items-center justify-between gap-3 bg-black/40 border border-white/10 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-white text-xs font-mono truncate">{f.id}</div>
                  <div className="text-white/50 text-xs truncate" title={f.error}>{f.error}</div>
                </div>
                <button
                  onClick={() => retryCancel([f.id])}
                  disabled={retrying}
                  data-testid={`retry-cancel-${f.id}`}
                  className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white text-xs flex items-center gap-1 shrink-0">
                  <RefreshCw size={12} /> Retry
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <FilterSelect value={query.user_id} onChange={v => { feed.setFilter('user_id', v); onProfileChange(v); }} testId="filter-user" options={[
          { v: 'all', l: 'All profiles' }, ...profiles.map(u => ({ v: u.user_id, l: u.email || u.name || u.user_id })),
        ]} />
        <FilterSelect value={query.status} onChange={v => feed.setFilter('status', v)} testId="filter-status" options={[
          { v: 'all', l: 'All posts' },
          { v: 'scheduled', l: 'Scheduled' }, { v: 'publishing', l: 'Publishing' }, { v: 'published', l: 'Published' },
          { v: 'failed', l: 'Failed' }, { v: 'cancelled', l: 'Cancelled' }, { v: 'draft', l: 'Draft' },
          { v: 'draft_uploaded', l: 'In TikTok drafts' },
        ]} />
        <FilterSelect value={query.platform} onChange={v => feed.setFilter('platform', v)} testId="filter-platform" options={[
          { v: 'all', l: 'All platforms' },
          { v: 'tiktok', l: 'TikTok' }, { v: 'instagram', l: 'Instagram' }, { v: 'facebook', l: 'Facebook' },
          { v: 'youtube', l: 'YouTube' }, { v: 'x', l: 'X (Twitter)' }, { v: 'linkedin', l: 'LinkedIn' },
        ]} />
        <FilterSelect value={query.date_range} onChange={v => feed.setFilter('date_range', v)} testId="filter-date" options={[
          { v: 'all', l: 'All dates' },
          { v: '7d', l: 'Last 7 days' }, { v: '30d', l: 'Last 30 days' }, { v: '90d', l: 'Last 90 days' },
        ]} />
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={feed.search}
          maxLength={200}
          onChange={(e) => feed.setSearch(e.target.value)}
          placeholder="Search content or user email…"
          data-testid="posts-search"
          className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none"
        />
      </div>

      {feed.profileError && <div data-testid="posts-profiles-error" role="alert" className="text-amber-300 text-sm mb-3">
        {feed.profileError} <button data-testid="posts-profiles-retry" onClick={feed.loadProfiles} className="underline ml-2">Retry profiles</button>
      </div>}
      {feed.error && <div data-testid="posts-load-error" role="alert" className="text-red-300 text-sm mb-3">
        {feed.error} <button data-testid="posts-load-retry" onClick={load} className="underline ml-2">Retry</button>
      </div>}
      {feed.syncError && <div data-testid="posts-sync-warning" role="status" className="text-amber-200/70 text-xs mb-3">{feed.syncError}</div>}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-2.5 mb-3" data-testid="bulk-action-bar">
          <div className="text-white text-sm">
            <span className="font-semibold">{selected.size} selected</span>
            {selected.size < filtered.length && (
              <button onClick={selectAll} data-testid="posts-select-page" className="ml-3 text-emerald-400 hover:underline text-xs">
                Select all {filtered.length} on this page
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={clearSelection} data-testid="posts-clear-selection" className="text-white/60 hover:text-white text-sm">Clear</button>
            <button
              onClick={bulkDelete}
              disabled={deleting}
              data-testid="bulk-delete-btn"
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold flex items-center gap-2 text-sm">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div data-testid="posts-loading" className="flex items-center justify-center py-12 text-white/50"><Loader2 className="animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div data-testid="posts-empty" className="text-center py-12 text-white/40">{feed.error ? 'Posts unavailable.' : 'No posts match these filters.'}</div>
      ) : (
        <>
          {/* Select-all helper */}
          <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
            <input
              type="checkbox"
              checked={selected.size > 0 && selected.size === filtered.length}
              onChange={e => e.target.checked ? selectAll() : clearSelection()}
              data-testid="select-all-checkbox"
              className="w-4 h-4 accent-emerald-400"
            />
            <span>Select all on screen</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map(p => {
              const picked = selected.has(p.id);
              const cs = computedStatus(p);
              const { label: timeLabel, value: timeVal } = timeInfo(p);
              const chips = platformChips(p);
              return (
                <div
                  key={p.id}
                  data-testid={`post-card-${p.id}`}
                  className={`bg-[#141414] border rounded-xl p-4 flex flex-col gap-2 cursor-pointer transition-colors ${picked ? 'border-emerald-500/60 ring-1 ring-emerald-500/40' : 'border-white/10 hover:border-white/20'}`}
                  onClick={() => toggle(p.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={picked}
                        onChange={() => toggle(p.id)}
                        onClick={e => e.stopPropagation()}
                        data-testid={`post-check-${p.id}`}
                        className="w-4 h-4 mt-1 accent-emerald-400 shrink-0"
                      />
                      <div data-testid={`post-caption-${p.id}`} className="text-white text-sm font-medium line-clamp-2 min-w-0 break-words">{p.content || '(no caption)'}</div>
                    </div>
                    <span data-testid={`post-status-${p.id}`} className={`text-xs px-2 py-0.5 rounded shrink-0 ${StatusPill(cs)}`}>{cs === 'draft_uploaded' ? 'TikTok drafts' : cs}</span>
                  </div>
                  <PostMedia post={p} />
                  {p.tiktok_publish_mode && <div data-testid={`post-tiktok-mode-${p.id}`} className="text-xs text-white/60">{p.tiktok_publish_mode === 'MEDIA_UPLOAD' ? 'TikTok · Draft upload' : 'TikTok · Direct publish'}</div>}
                  <div className="text-white/40 text-xs" data-testid={`post-time-${p.id}`}>
                    {timeLabel && <span className="text-white/60 font-medium mr-1">{timeLabel}</span>}{timeVal}
                  </div>
                  {p.error_message && (
                    <div className="text-red-400/80 text-[11px] line-clamp-2" title={p.error_message} data-testid={`post-error-${p.id}`}>{p.error_message}</div>
                  )}
                  <div data-testid={`post-owner-${p.id}`} className="text-white/50 text-xs truncate flex items-center gap-1"><UserIcon size={11} /> {p.user_email || p.user_id || '—'}{p.created_by_admin && <span className="ml-1 text-emerald-400">(admin)</span>}</div>
                  {chips.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      {chips.map((c, i) => (
                        <span
                          key={`${c.platform}-${c.username || i}`}
                          data-testid={`post-chip-${p.id}-${c.platform}-${i}`}
                          className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"
                          title={c.error ? `${c.platform}${c.username ? ' · @' + c.username : ''}: ${c.error}` : `${c.platform}${c.username ? ' · @' + c.username : ''}${c.status ? ' · ' + c.status : ''}`}
                        >
                          <PlatformLogo platform={c.platform} size={12} />
                          {c.username ? (
                            <span className="text-white/70 text-[10px]">@{c.username}</span>
                          ) : (
                            <span className="text-white/60 text-[10px] capitalize">{c.platform}</span>
                          )}
                          {c.status && <span className={`w-1.5 h-1.5 rounded-full ${dotColor(c.status)}`} />}
                        </span>
                      ))}
                    </div>
                  )}
                  <div data-testid={`post-id-${p.id}`} className="text-white/30 text-[10px] font-mono truncate">{p.id?.slice(0, 8)}…</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <nav aria-label="Post pages" data-testid="posts-pagination" className="flex flex-wrap items-center justify-between gap-3 mt-5 text-sm">
        <span data-testid="posts-page-info" className="text-white/50">Page {page} of {Math.max(1, Math.ceil(total / 24))} · {posts.length} of {total} posts</span>
        <div className="flex gap-2">
          <button data-testid="posts-prev-page" disabled={page <= 1 || loading} onClick={() => feed.setPage(page - 1)} className="px-3 py-2 rounded-lg bg-white/5 text-white/80 hover:bg-white/10 disabled:opacity-30">Previous</button>
          <button data-testid="posts-next-page" disabled={!hasMore || loading} onClick={() => feed.setPage(page + 1)} className="px-3 py-2 rounded-lg bg-white/5 text-white/80 hover:bg-white/10 disabled:opacity-30">Next</button>
        </div>
      </nav>

      {showCreate && <CreatePostModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} toast={toast} />}
    </div>
  );
};

const FilterSelect = ({ value, onChange, options, testId }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    data-testid={testId}
    className="max-w-full min-w-0 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-emerald-500 focus:outline-none">
    {options.map(o => <option key={o.v} value={o.v} className="bg-[#141414]">{o.l}</option>)}
  </select>
);


const CreatePostModal = ({ onClose, onCreated, toast }) => {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [userAccounts, setUserAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [content, setContent] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [tiktokMode, setTiktokMode] = useState('DIRECT_POST');
  const [tiktokPrivacy, setTiktokPrivacy] = useState('');
  const [publishNow, setPublishNow] = useState(true);
  const [scheduleTime, setScheduleTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submitInFlight = useRef(false);
  const hasTikTok = userAccounts.some(a => selectedAccounts.includes(a.id) && (a.platform || '').toLowerCase() === 'tiktok');
  const draftUpload = hasTikTok && tiktokMode === 'MEDIA_UPLOAD';

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const r = await fetchAdmin('/api/admin-panel/post-profiles', { signal: controller.signal });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Could not load profiles');
        if (!controller.signal.aborted) setUsers((d.users || []).filter(u => u.has_profile));
      } catch (e) { if (e.name !== 'AbortError') setAccountsError(e.message); }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setUserAccounts([]);
    setSelectedAccounts([]);
    setTiktokMode('DIRECT_POST');
    setTiktokPrivacy('');
    if (!userId) { setAccountsLoading(false); return undefined; }
    const controller = new AbortController();
    setAccountsLoading(true);
    setAccountsError('');
    fetchAdmin(`/api/admin-panel/post-profiles/${encodeURIComponent(userId)}/accounts`, { signal: controller.signal })
      .then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Could not load this profile’s accounts');
        if (!controller.signal.aborted) setUserAccounts(d.accounts || []);
      }).catch(e => { if (e.name !== 'AbortError') setAccountsError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setAccountsLoading(false); });
    return () => controller.abort();
  }, [userId]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/admin-panel/media/upload`, {
        method: 'POST', headers: { 'X-Admin-Token': token }, body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload failed');
      setMediaUrls(m => [...m, d.publicUrl]);
      toast(`Uploaded ${file.name}`, true);
    } catch (e) { toast(`Upload failed: ${e.message}`, false); }
    setUploading(false);
  };

  const submit = async () => {
    if (submitting || submitInFlight.current) return;
    if (!userId) return toast('Pick a target user', false);
    if (selectedAccounts.length === 0) return toast('Select at least one account', false);
    if (hasTikTok && tiktokMode === 'DIRECT_POST' && !tiktokPrivacy) return toast('Select TikTok visibility', false);
    if (hasTikTok && !mediaUrls.length) return toast('TikTok requires an image or video', false);
    submitInFlight.current = true;
    setSubmitting(true);
    try {
      const r = await fetchAdmin('/api/admin-panel/publish', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          content,
          media_urls: mediaUrls,
          account_ids: selectedAccounts,
          publish_now: publishNow,
          schedule_time: publishNow ? null : (scheduleTime ? new Date(scheduleTime).toISOString() : null),
          ...(hasTikTok ? { platform_config: tiktokConfig(tiktokMode, tiktokPrivacy) } : {}),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Publish failed');
      toast(draftUpload ? (publishNow ? 'Submitted for TikTok draft upload — finish in TikTok after delivery.' : 'TikTok draft upload scheduled.') : (publishNow ? 'Submitted for publishing.' : 'Scheduled!'), true);
      onCreated();
    } catch (e) { toast(`Failed: ${e.message}`, false); }
    finally { submitInFlight.current = false; setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141414] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">Create post on user's behalf</h3>
          <button onClick={onClose} data-testid="create-post-close" className="p-1 text-white/60 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-white/60 text-xs mb-1 block">Target user</label>
            <select
              value={userId}
              onChange={e => setUserId(e.target.value)}
              data-testid="create-post-user"
              className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-emerald-500 focus:outline-none">
              <option value="">— Pick a profile ({users.length}) —</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id} className="bg-[#141414]">
                  {u.email || u.name || u.user_id}
                </option>
              ))}
            </select>
          </div>

          {accountsLoading && <div data-testid="create-post-accounts-loading" className="text-white/50 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading accounts…</div>}
          {accountsError && <div data-testid="create-post-accounts-error" role="alert" className="text-red-300 text-sm">{accountsError}</div>}
          {userId && !accountsLoading && !accountsError && !userAccounts.length && <div data-testid="create-post-no-accounts" className="text-white/50 text-sm">No connected accounts for this profile.</div>}
          {userAccounts.length > 0 && (
            <div>
              <label className="text-white/60 text-xs mb-1 block">Accounts to post to</label>
              <div className="flex flex-wrap gap-2">
                {userAccounts.map(a => {
                  const picked = selectedAccounts.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`account-pick-${a.id}`}
                      onClick={() => setSelectedAccounts(s => picked ? s.filter(x => x !== a.id) : [...s, a.id])}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${picked ? 'bg-emerald-500/10 border-emerald-500/50 text-white' : 'bg-white/5 border-white/10 text-white/70'}`}>
                      <PlatformLogo platform={a.platform} size={14} />
                      <span>{a.username || a.display_name || a.platform}</span>
                      {picked && <Check size={14} className="text-emerald-400" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="text-white/60 text-xs mb-1 block">Caption</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={4}
              placeholder="What are we posting?"
              data-testid="create-post-content"
              className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none" />
          </div>

          <div>
            <label className="text-white/60 text-xs mb-1 block">Media</label>
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-dashed border-white/20 cursor-pointer hover:border-emerald-500/50 text-white/70 w-fit">
              <Upload size={14} />
              <span className="text-sm">{uploading ? 'Uploading…' : 'Upload image/video'}</span>
              <input
                type="file"
                accept="image/*,video/*"
                data-testid="create-post-media"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }}
              />
            </label>
            {mediaUrls.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {mediaUrls.map((url, i) => (
                  <div key={i} className="relative group">
                    {/\.(mp4|mov|webm|m4v)(\?|$)/i.test(url) ? (
                      <video src={url} preload="none" controls playsInline className="w-24 h-24 object-contain rounded-lg" data-testid={`create-post-video-${i}`} />
                    ) : (
                      <img src={url} alt="Uploaded attachment" loading="lazy" decoding="async" className="w-24 h-24 object-contain rounded-lg" data-testid={`create-post-image-${i}`} />
                    )}
                    <button
                      onClick={() => setMediaUrls(m => m.filter((_, j) => j !== i))}
                      data-testid={`create-post-remove-media-${i}`} aria-label="Remove attachment"
                      className="absolute -top-1 -right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {hasTikTok && <TikTokPublishOptions prefix="admin" mode={tiktokMode} privacy={tiktokPrivacy}
            onModeChange={mode => { setTiktokMode(mode); setTiktokPrivacy(''); }} onPrivacyChange={setTiktokPrivacy} disabled={submitting} />}

          <div>
            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
              <input type="checkbox" checked={publishNow} onChange={e => setPublishNow(e.target.checked)} data-testid="create-post-now" />
              {draftUpload ? 'Send immediately' : 'Publish immediately'}
            </label>
            {!publishNow && (
              <input
                type="datetime-local"
                value={scheduleTime}
                onChange={e => setScheduleTime(e.target.value)}
                data-testid="create-post-schedule"
                className="mt-2 w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-emerald-500 focus:outline-none" />
            )}
          </div>
        </div>

        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} data-testid="create-post-cancel" className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !userId || selectedAccounts.length === 0}
            data-testid="create-post-submit"
            className="px-5 py-2 rounded-xl bg-emerald-400 hover:bg-emerald-500 disabled:opacity-50 text-black font-semibold flex items-center gap-2 text-sm">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {draftUpload ? (publishNow ? 'Send to TikTok drafts' : 'Schedule draft upload') : (publishNow ? 'Publish now' : 'Schedule')}
          </button>
        </div>
      </div>
    </div>
  );
};


// ===== API Keys tab (v1 master keys) =====
const ApiKeysTab = ({ toast }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // one-time reveal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchAdmin('/api/admin-panel/api-keys');
      const d = await r.json();
      setKeys(d.keys || []);
    } catch (e) { toast(`Load failed: ${e.message}`, false); }
    setLoading(false);
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const r = await fetchAdmin('/api/admin-panel/api-keys', {
        method: 'POST', body: JSON.stringify({ name }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setNewKey(d);
      setName('');
      load();
    } catch (e) { toast(`Create failed: ${e.message}`, false); }
    setCreating(false);
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Apps using this key will immediately get 401.`)) return;
    try {
      const r = await fetchAdmin(`/api/admin-panel/api-keys/${k.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      toast(`Revoked ${k.name}`, true);
      load();
    } catch (e) { toast(`Revoke failed: ${e.message}`, false); }
  };

  return (
    <div data-testid="admin-api-keys">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">API Keys</h2>
          <p data-testid="api-keys-scope-description" className="text-white/50 text-sm mt-1">Master keys for the public v1 API — <code className="text-emerald-400">/api/v1/*</code>, authenticated with <code className="text-emerald-400">Authorization: Bearer apk_…</code>. Discover all profiles with <code className="text-emerald-400">/accounts/all</code>; publish using each account’s owning <code className="text-emerald-400">user_id</code>. Full developer reference below.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-[#141414] border border-white/10 rounded-xl p-4 mb-4">
        <label className="text-white/60 text-xs mb-1 block">Create new API key</label>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. ContentOS Production"
            data-testid="apikey-name"
            className="flex-1 p-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500 focus:outline-none text-sm"
          />
          <button
            onClick={create}
            disabled={creating || !name.trim()}
            data-testid="apikey-create"
            className="px-4 py-2.5 rounded-lg bg-emerald-400 hover:bg-emerald-500 disabled:opacity-50 text-black font-semibold flex items-center gap-2 text-sm">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </button>
        </div>
      </div>

      {newKey && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 mb-4" data-testid="apikey-reveal">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="text-amber-300 font-semibold text-sm flex items-center gap-2"><Key size={14} /> Copy this key now — it's shown only once</div>
              <div className="text-white/60 text-xs mt-1">After closing this box the key cannot be retrieved again. Revoke & create a new one if lost.</div>
            </div>
            <button onClick={() => setNewKey(null)} className="p-1 text-white/60 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <code className="flex-1 p-3 rounded-lg bg-black/40 text-emerald-300 text-sm font-mono break-all">{newKey.key}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(newKey.key); toast('Copied', true); }}
              className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-1">
              <Copy size={14} /> Copy
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/50"><Loader2 className="animate-spin" /></div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12 text-white/40">No API keys yet. Create one above.</div>
      ) : (
        <div className="space-y-2">
          {keys.map(k => (
            <div key={k.id} data-testid={`apikey-row-${k.id}`} className="bg-[#141414] border border-white/10 rounded-xl p-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{k.name}</span>
                  {k.revoked && <span className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400">revoked</span>}
                </div>
                <div className="text-white/40 text-xs mt-1 font-mono">{k.preview}</div>
                <div className="text-white/30 text-xs mt-0.5">
                  Created {new Date(k.created_at).toLocaleString()}
                  {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleString()}`}
                </div>
              </div>
              {!k.revoked && (
                <button
                  onClick={() => revoke(k)}
                  data-testid={`apikey-revoke-${k.id}`}
                  className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <ApiDocs />
    </div>
  );
};


// ===== Analytics tab (with filters + charts + click-through drill-down) =====
const PLATFORM_COLORS = {
  tiktok: '#69c9d0', instagram: '#ec4899', facebook: '#3b82f6',
  youtube: '#ef4444', x: '#9ca3af', linkedin: '#0a66c2',
};

const AnalyticsTab = ({ toast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('all');
  const [range, setRange] = useState('30d'); // 7d | 30d | 90d
  const [focus, setFocus] = useState(null);  // { account_id, username, platform, user_email } — null = all accounts

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const days = { '7d': 7, '30d': 30, '90d': 90 }[range];
      const since = new Date(now.getTime() - days * 86400000).toISOString();
      const params = new URLSearchParams({ since, until: now.toISOString(), limit: '10' });
      if (platform !== 'all') params.set('platform', platform);
      if (focus?.account_id) params.set('channel_id', focus.account_id);
      if (focus?.user_id) params.set('user_id', focus.user_id);
      const r = await fetchAdmin(`/api/admin-panel/analytics?${params.toString()}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setData(d);
    } catch (e) { toast(`Load failed: ${e.message}`, false); }
    setLoading(false);
  }, [toast, platform, range, focus]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const byPlatform = data?.byPlatform || [];
  const topPosts = data?.topPosts || [];
  const perAccount = data?.perAccount || [];
  const accountCount = data?.account_count ?? perAccount.length;

  const postsByPlatform = byPlatform.map(x => ({
    platform: x.platform || x.name || 'unknown',
    posts: x.posts || x.postCount || 0,
    likes: x.likes || 0,
    views: x.views || 0,
  }));

  const engagement = totals.engagementRate ?? (totals.views ? ((totals.likes || 0) / totals.views * 100) : null);
  const bestPost = topPosts[0];

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Analytics</h2>
          <p className="text-white/50 text-sm mt-1">Posts, reach and engagement across every connected account.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <FilterSelect value={platform} onChange={setPlatform} testId="analytics-platform" options={[
          { v: 'all', l: 'All platforms' },
          { v: 'tiktok', l: 'TikTok' }, { v: 'instagram', l: 'Instagram' }, { v: 'facebook', l: 'Facebook' },
          { v: 'youtube', l: 'YouTube' }, { v: 'x', l: 'X (Twitter)' }, { v: 'linkedin', l: 'LinkedIn' },
        ]} />
        <FilterSelect value={range} onChange={setRange} testId="analytics-range" options={[
          { v: '7d', l: 'Last 7 days' }, { v: '30d', l: 'Last 30 days' }, { v: '90d', l: 'Last 90 days' },
        ]} />
        {focus && (
          <div data-testid="analytics-focus-chip" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm">
            <PlatformLogo platform={focus.platform} size={14} />
            <span>Focused: <span className="font-semibold">{focus.username || focus.account_id?.slice(0, 10)}</span></span>
            <span className="text-emerald-400/60 text-xs">· {focus.user_email}</span>
            <button
              onClick={() => setFocus(null)}
              data-testid="analytics-focus-clear"
              className="ml-1 p-0.5 rounded hover:bg-emerald-500/20">
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/50"><Loader2 className="animate-spin" /></div>
      ) : !data ? (
        <div className="text-center py-24 text-white/40">No analytics data.</div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <AnalyticsStatCard label="Engagement rate" value={engagement != null ? `${engagement.toFixed(1)}%` : '—'} />
            <AnalyticsStatCard label="Total reach" value={fmt(totals.views)} icon={Eye} />
            <AnalyticsStatCard label="Total followers" value={fmt(totals.followers)} icon={Users} />
            <AnalyticsStatCard label="Posts this period" value={fmt(totals.posts)} icon={FileText} />
            <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
              <div className="text-white/50 text-xs mb-1">Best post</div>
              {bestPost ? (
                <>
                  <div className="text-white font-semibold text-lg">{fmt(bestPost.likes || bestPost.engagement || 0)}</div>
                  <a href={bestPost.url || bestPost.permalink || '#'} target="_blank" rel="noreferrer" className="text-emerald-400 text-xs hover:underline">View ↗</a>
                </>
              ) : <div className="text-white/40 text-sm">—</div>}
            </div>
          </div>

          {/* Posts per platform */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <ChartCard title="Posts per platform" subtitle={`${postsByPlatform.length} platforms`} total={totals.posts}>
              {postsByPlatform.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={postsByPlatform}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                    <XAxis dataKey="platform" tick={{ fill: '#ffffff80', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#ffffff60', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#111', border: '1px solid #ffffff20', borderRadius: 8 }} />
                    <Bar dataKey="posts" radius={[6, 6, 0, 0]}>
                      {postsByPlatform.map((entry, i) => (
                        <rect key={i} fill={PLATFORM_COLORS[entry.platform?.toLowerCase?.()] || '#10b981'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyChart />}
            </ChartCard>

            <ChartCard title="Likes per platform" subtitle="Engagement signal">
              {postsByPlatform.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={postsByPlatform}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                    <XAxis dataKey="platform" tick={{ fill: '#ffffff80', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#ffffff60', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#111', border: '1px solid #ffffff20', borderRadius: 8 }} />
                    <Bar dataKey="likes" fill="#ec4899" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyChart />}
            </ChartCard>
          </div>

          {/* Per-account breakdown */}
          <div className="bg-[#141414] border border-white/10 rounded-xl p-4 mb-4" data-testid="analytics-per-account">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold">Per-account analytics</h3>
                <p className="text-white/40 text-xs">{accountCount} linked {accountCount === 1 ? 'account' : 'accounts'} across every AutoPostr user</p>
              </div>
            </div>
            {perAccount.length === 0 ? (
              <div className="text-white/40 text-sm py-6 text-center">No connected accounts in scope.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/50 text-xs uppercase tracking-wide text-left border-b border-white/10">
                      <th className="py-2 pr-3">Account</th>
                      <th className="py-2 pr-3">User</th>
                      <th className="py-2 pr-3 text-right">Posts</th>
                      <th className="py-2 pr-3 text-right">Views</th>
                      <th className="py-2 pr-3 text-right">Likes</th>
                      <th className="py-2 text-right">Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perAccount.map(a => (
                      <tr
                        key={a.id}
                        onClick={() => setFocus({ account_id: a.id, username: a.username, platform: a.platform, user_email: a.user_email, user_id: a.user_id })}
                        data-testid={`analytics-row-${a.id}`}
                        className={`border-b border-white/5 last:border-0 hover:bg-emerald-500/5 cursor-pointer ${focus?.account_id === a.id ? 'bg-emerald-500/10' : ''}`}
                        title="Click to focus analytics on this account"
                      >
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <PlatformLogo platform={a.platform} size={14} />
                            <span className="text-white font-medium">{a.username || a.id?.slice(0, 8)}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-white/60 text-xs truncate max-w-[220px]">{a.user_email || a.user_id}</td>
                        <td className="py-2 pr-3 text-right text-white">{fmt(a.totals?.posts || 0)}</td>
                        <td className="py-2 pr-3 text-right text-white">{fmt(a.totals?.views || 0)}</td>
                        <td className="py-2 pr-3 text-right text-white">{fmt(a.totals?.likes || 0)}</td>
                        <td className="py-2 text-right text-white">{fmt(a.totals?.comments || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Top posts table */}
          <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-3">Top posts</h3>
            {topPosts.length === 0 ? (
              <div className="text-white/40 text-sm py-6 text-center">No top posts in this window.</div>
            ) : (
              <div className="space-y-2">
                {topPosts.slice(0, 10).map((p, i) => (
                  <div key={p.id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                    <span className="text-white/40 text-sm font-mono w-6">{i + 1}</span>
                    <PlatformLogo platform={p.platform} size={14} />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">{p.text || p.caption || p.content || '(no caption)'}</div>
                      <div className="text-white/40 text-xs">{p.platform} · {p.publishedAt ? new Date(p.publishedAt).toLocaleDateString() : '—'}</div>
                    </div>
                    <div className="text-right text-white text-sm shrink-0">
                      <div className="flex items-center justify-end gap-1"><Heart size={11} className="text-pink-400" /> {fmt(p.likes || 0)}</div>
                      <div className="text-white/40 text-xs flex items-center justify-end gap-1"><Eye size={11} /> {fmt(p.views || 0)}</div>
                    </div>
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-white/60" title="Open post">
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const fmt = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
};

const AnalyticsStatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
    <div className="text-white/50 text-xs mb-1 flex items-center gap-1">{Icon && <Icon size={11} />}{label}</div>
    <div className="text-white font-bold text-xl">{value}</div>
  </div>
);

const ChartCard = ({ title, subtitle, total, children }) => (
  <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
    <div className="flex items-start justify-between mb-3">
      <div>
        <h3 className="text-white font-semibold">{title}</h3>
        {subtitle && <p className="text-white/40 text-xs">{subtitle}</p>}
      </div>
      {total != null && <div className="text-right"><div className="text-white font-bold">{fmt(total)}</div><div className="text-white/40 text-[10px]">total</div></div>}
    </div>
    {children}
  </div>
);

const EmptyChart = () => (
  <div className="h-[260px] flex items-center justify-center text-white/30 text-sm">No data in this window.</div>
);


// ===== Main =====
const AdminPanelPage = () => {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState('connections');
  const [postProfile, setPostProfile] = useState('all');
  const [toastMsg, setToastMsg] = useState(null);

  const verify = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    const r = await fetch(`${API_URL}/api/admin-panel/me`, { headers: { 'X-Admin-Token': token } });
    return r.ok;
  }, []);

  useEffect(() => { verify().then(setAuthed); }, [verify]);

  const toast = useCallback((msg, ok = true) => {
    setToastMsg({ msg, ok });
    setTimeout(() => setToastMsg(null), 6000);
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  const navItems = [
    { id: 'connections', label: 'Connections', icon: Link2 },
    { id: 'posts', label: 'Posts', icon: FileText },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'campaigns', label: 'Campaigns', icon: Mail },
    { id: 'pro-watcher', label: 'Pro Watcher', icon: Sparkles },
    { id: 'settings', label: 'SMTP Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Sidebar */}
      <aside className="w-14 sm:w-60 bg-[#0f0f0f] border-r border-white/10 flex flex-col shrink-0">
        <div className="p-2 sm:p-4 border-b border-white/10 flex items-center justify-center sm:justify-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck size={18} className="text-emerald-400" />
          </div>
          <div className="min-w-0 hidden sm:block">
            <div className="text-white text-sm font-bold truncate">AutoPostr Admin</div>
            <div className="text-white/40 text-xs truncate">Full control</div>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map(n => {
            const Icon = n.icon;
            const active = tab === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setTab(n.id)}
                data-testid={`sidebar-${n.id}`}
                aria-label={n.label} title={n.label} aria-current={active ? 'page' : undefined}
                className={`w-full flex items-center justify-center sm:justify-start gap-3 px-2 sm:px-3 py-2.5 rounded-lg text-sm transition-colors ${active ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`}>
                <Icon size={16} className="shrink-0" />
                <span className="hidden sm:inline">{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-2 border-t border-white/10">
          <button onClick={logout} data-testid="sidebar-logout" aria-label="Sign out" title="Sign out" className="w-full flex items-center justify-center sm:justify-start gap-3 px-2 sm:px-3 py-2.5 rounded-lg text-sm text-white/50 hover:text-white hover:bg-white/5">
            <LogOut size={16} className="shrink-0" /><span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
          {tab === 'connections' && <ConnectionsTab toast={toast} />}
          {tab === 'posts' && <PostsTab toast={toast} profileId={postProfile} onProfileChange={setPostProfile} />}
          {tab === 'calendar' && <PostCalendarTab fetchAdmin={fetchAdmin} profileId={postProfile} onProfileChange={setPostProfile} />}
          {tab === 'analytics' && <AnalyticsTab toast={toast} />}
          {tab === 'users' && <UsersTab toast={toast} />}
          {tab === 'api-keys' && <ApiKeysTab toast={toast} />}
          {tab === 'campaigns' && <CampaignsTab toast={toast} />}
          {tab === 'pro-watcher' && <ProWatcherTab toast={toast} />}
          {tab === 'settings' && <SettingsTab toast={toast} />}
        </div>
      </main>

      {toastMsg && (
        <div data-testid="admin-toast" className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-2xl border z-50 flex items-center gap-2 ${toastMsg.ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
          {toastMsg.ok ? <Check size={16} /> : <AlertCircle size={16} />}
          <span className="text-sm">{toastMsg.msg}</span>
        </div>
      )}
    </div>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${active ? 'border-emerald-500 text-white' : 'border-transparent text-white/50 hover:text-white'}`}>
    <Icon size={14} /> {label}
  </button>
);

export default AdminPanelPage;
